import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, Settings, MessageSquare, LogOut, Building2, Check, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import {
  useTheme,
  THEME_AUTO_DAY_START_HOUR,
  THEME_AUTO_DAY_END_HOUR,
} from '@contexts/ThemeContext';
import { useAuth } from '@core/auth/useAuth';

interface TopbarAccountMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function nameInitials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function roleLabel(role: string): string {
  return (
    {
      owner: 'Owner',
      admin: 'Admin',
      senior_recruiter: 'Senior recruiter',
      junior_recruiter: 'Junior recruiter',
      viewer: 'Viewer',
    }[role] ?? role
  );
}

export function TopbarAccountMenu({ open, onOpenChange }: TopbarAccountMenuProps) {
  const { theme, themeMode, toggleTheme, ianaTimeZone } = useTheme();
  const { user, logout, isLoading, allOrgs, switchOrg } = useAuth();
  const accountBtnRef = useRef<HTMLButtonElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [showWorkspaces, setShowWorkspaces] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowWorkspaces(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (accountBtnRef.current?.contains(t) || accountMenuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, onOpenChange]);

  const multiOrg = allOrgs.length > 1;

  return (
    <div className="relative">
      <button
        ref={accountBtnRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-2 rounded-full border vscode-border p-0.5 pr-2 transition-colors hover:vscode-bg-hover"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--vscode-primary)_18%,transparent)] text-[11px] font-semibold vscode-text-primary">
          {isLoading ? (
            <span className="inline-block h-3 w-3 animate-pulse rounded-full vscode-bg-tertiary" />
          ) : (
            nameInitials(user?.name)
          )}
        </div>
        <span className="hidden max-w-[160px] truncate text-[11px] font-medium vscode-text-secondary md:inline">
          {isLoading ? '…' : (user?.name ?? user?.email ?? 'Account')}
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[100]" aria-hidden onClick={() => onOpenChange(false)} />
          <div
            ref={accountMenuRef}
            role="menu"
            className="absolute right-0 top-full z-[110] mt-2 w-[min(calc(100vw-2rem),280px)] overflow-hidden rounded-lg border vscode-border vscode-bg-elevated shadow-2xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_50%,transparent)]"
          >
            {isLoading ? (
              <div className="space-y-2 px-3 py-3" aria-busy="true">
                <div className="h-3 w-40 animate-pulse rounded vscode-bg-tertiary" />
                <div className="h-2 w-28 animate-pulse rounded vscode-bg-tertiary" />
              </div>
            ) : !user ? (
              <p className="px-3 py-4 text-center text-[11px] vscode-text-tertiary">Not signed in.</p>
            ) : (
              <>
                {/* ── User identity ── */}
                <div className="border-b vscode-border px-3 pb-3 pt-2">
                  <p className="truncate text-[13px] font-semibold vscode-text-primary">
                    {user.name || user.email}
                  </p>
                  {user.email && user.name ? (
                    <p className="mt-0.5 truncate text-[11px] vscode-text-tertiary">{user.email}</p>
                  ) : null}
                </div>

                {/* ── Active workspace / switcher ── */}
                <div className="border-b vscode-border">
                  <button
                    type="button"
                    role="menuitem"
                    aria-expanded={showWorkspaces}
                    onClick={() => multiOrg && setShowWorkspaces((v) => !v)}
                    className={[
                      'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] vscode-text-secondary transition-colors',
                      multiOrg ? 'hover:vscode-bg-hover hover:vscode-text-primary cursor-pointer' : 'cursor-default',
                    ].join(' ')}
                  >
                    <Building2 className="h-4 w-4 flex-shrink-0 vscode-text-tertiary" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] font-semibold vscode-text-primary">
                        {user.orgName ?? 'No workspace'}
                      </p>
                      {user.roles?.[0] && (
                        <p className="text-[10.5px] vscode-text-tertiary">{roleLabel(user.roles[0])}</p>
                      )}
                    </div>
                    {multiOrg && (
                      <span className="ml-auto flex-shrink-0 vscode-text-tertiary" aria-hidden>
                        {showWorkspaces ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </span>
                    )}
                  </button>

                  {/* Workspace list — slides open */}
                  {showWorkspaces && (
                    <div className="border-t vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_60%,transparent)] pb-1">
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                        Your workspaces
                      </p>
                      {allOrgs.map(({ org, role }) => {
                        const isActive = org.id === user.orgId;
                        return (
                          <button
                            key={org.id}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              if (!isActive) {
                                switchOrg(org.id);
                                onOpenChange(false);
                                // Reload so all data queries pick up the new org
                                window.location.href = '/dashboard';
                              }
                            }}
                            className={[
                              'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors',
                              isActive
                                ? 'vscode-text-primary cursor-default'
                                : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary cursor-pointer',
                            ].join(' ')}
                          >
                            <span
                              className={[
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold',
                                isActive
                                  ? 'bg-[color-mix(in_srgb,var(--vscode-primary)_22%,transparent)] text-[var(--vscode-primary)]'
                                  : 'vscode-bg-tertiary vscode-text-tertiary',
                              ].join(' ')}
                            >
                              {org.name.slice(0, 1).toUpperCase()}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium leading-tight">{org.name}</p>
                              <p className="text-[10.5px] vscode-text-tertiary">{roleLabel(role)}</p>
                            </div>
                            {isActive && (
                              <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-[var(--vscode-primary)]" aria-label="Active" />
                            )}
                          </button>
                        );
                      })}
                      {/* Create new workspace */}
                      <Link
                        to="/onboarding"
                        role="menuitem"
                        onClick={() => onOpenChange(false)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-[12.5px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md vscode-bg-tertiary vscode-text-tertiary">
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                        </span>
                        <span className="font-medium">Create new workspace</span>
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── Settings rows ── */}
                <div className="py-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => toggleTheme()}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4 gg-toolbar-ic--amber" aria-hidden />
                    ) : (
                      <Moon className="h-4 w-4 gg-toolbar-ic--indigo" aria-hidden />
                    )}
                    <span>
                      {themeMode === 'auto'
                        ? `Theme: auto (${THEME_AUTO_DAY_START_HOUR}:00–${THEME_AUTO_DAY_END_HOUR}:00 light, ${ianaTimeZone})`
                        : themeMode === 'light'
                          ? 'Theme: light'
                          : 'Theme: dark'}
                    </span>
                  </button>

                  <Link
                    role="menuitem"
                    to="/settings/profile"
                    onClick={() => onOpenChange(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                  >
                    <Settings className="h-4 w-4 flex-shrink-0" aria-hidden />
                    Settings
                  </Link>

                  <a
                    role="menuitem"
                    href="mailto:support@gograbjob.com"
                    className="flex items-center gap-2 px-3 py-2.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                    onClick={() => onOpenChange(false)}
                  >
                    <MessageSquare className="h-4 w-4 flex-shrink-0" aria-hidden />
                    Support
                  </a>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onOpenChange(false);
                      logout();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:text-red-400"
                  >
                    <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
